import { A2uiComponentEntry } from 'types/space';
import { CATALOG } from '@center-platform/component-catalog';
import { ConformanceFinding, makeFinding } from '../findings';

/** The slice of a schema property `checkDataContract` needs — never the full `BaseProperty` shape (schema.d.ts), which drags in edit/render metadata this pure check has no use for. */
export interface SchemaPropertyInfo {
  /** `PropertyDataType`-ish (schema.d.ts `BaseProperty.type`); kept as `string` so an unrecognized type degrades to "no type check" rather than a compile error. */
  type: string;
  access?: { read?: unknown };
}

/** Resolves a schema's properties by system name; `undefined` means the schema itself could not be resolved (not just "property not found"). */
export type SchemaResolver = (schemaSystemName: string) => Record<string, SchemaPropertyInfo> | undefined;

/**
 * `BaseProperty.type` (schema.d.ts `PropertyDataType`) -> the catalog `PropDef.type` vocabulary
 * (`component-catalog/types.ts`, the same closed set `interfaceConformance.ts`'s
 * `JSON_TYPE_CHECKS` enforces). A schema type with no entry here (`json`) is intentionally
 * permissive — it has no single JS shape to compare against a catalog prop type.
 */
const SCHEMA_TO_CATALOG_TYPE: Partial<Record<string, string>> = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  date: 'string',
  datetime: 'string',
  reference: 'string',
  unit: 'number',
  array: 'string[]',
};

/** Sphere fields every schema instance carries regardless of its declared `properties` — never schema-lookup misses. */
const WELL_KNOWN_SPHERE_ROOTS = new Set(['id', 'name', 'type', 'status']);

/** Catalog prop types with an actual JS shape to compare a schema property's type against — mirrors `interfaceConformance.ts`'s `JSON_TYPE_CHECKS`. A catalog type outside this set (e.g. `BoundValue`, an inline object shape) has no single shape and is left permissive, exactly as `matchesDeclaredType` treats it. */
const CHECKABLE_CATALOG_TYPES = new Set(['string', 'number', 'boolean', 'string[]']);

function findCatalogPropType(componentType: string, fieldName: string): string | undefined {
  return CATALOG.find((entry) => entry.type === componentType)?.props[fieldName]?.type;
}

function checkSpherePointer(
  pointer: string,
  componentType: string,
  fieldName: string,
  schemaSystemName: string,
  schemaProperties: Record<string, SchemaPropertyInfo> | undefined,
  path: string
): ConformanceFinding[] {
  const suffix = pointer.slice('/sphere/'.length);
  if (!suffix.startsWith('properties/')) {
    if (WELL_KNOWN_SPHERE_ROOTS.has(suffix)) {
      // A well-known root (`/sphere/id`, `/sphere/name`, ...) — always resolvable; no
      // catalog-level type contract is declared for these here, so there is nothing further to check.
      return [];
    }
    return [
      makeFinding(
        'DATA_PATH_UNRESOLVED',
        'L3',
        'error',
        `Binding "${pointer}" does not resolve to a declared sphere property or a well-known root`,
        { path }
      ),
    ];
  }

  const propName = suffix.slice('properties/'.length);

  if (!schemaProperties) {
    return [
      makeFinding(
        'DATA_PATH_UNRESOLVED',
        'L3',
        'error',
        `Binding "${pointer}" declares schema "${schemaSystemName}", which could not be resolved`,
        { path }
      ),
    ];
  }

  const property = schemaProperties[propName];
  if (!property) {
    return [
      makeFinding(
        'DATA_PATH_UNRESOLVED',
        'L3',
        'error',
        `Binding "${pointer}" references property "${propName}", which schema "${schemaSystemName}" does not declare`,
        { path }
      ),
    ];
  }

  const findings: ConformanceFinding[] = [];
  const catalogType = findCatalogPropType(componentType, fieldName);
  const expectedCatalogType = SCHEMA_TO_CATALOG_TYPE[property.type];
  if (catalogType && CHECKABLE_CATALOG_TYPES.has(catalogType) && expectedCatalogType && catalogType !== expectedCatalogType) {
    findings.push(
      makeFinding(
        'DATA_PATH_TYPE_MISMATCH',
        'L3',
        'error',
        `Binding "${pointer}" resolves to schema property "${propName}" (type "${property.type}"), incompatible with "${fieldName}"'s expected type "${catalogType}"`,
        { path }
      )
    );
  }

  if (property.access?.read) {
    findings.push(
      makeFinding(
        'DATA_PATH_ACCESS_GATED',
        'L3',
        'warning',
        `Binding "${pointer}" resolves to property "${propName}", which carries read-access rules; render will be viewer-dependent`,
        { path }
      )
    );
  }

  return findings;
}

/** Collects every `{ $bind: '/sphere/...' }` pointer nested anywhere inside one field's value. */
function checkFieldBindings(
  value: unknown,
  componentType: string,
  fieldName: string,
  schemaSystemName: string,
  schemaProperties: Record<string, SchemaPropertyInfo> | undefined,
  path: string
): ConformanceFinding[] {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const bindPointer = (value as { $bind?: unknown }).$bind;
    if (typeof bindPointer === 'string' && bindPointer.startsWith('/sphere/')) {
      return checkSpherePointer(bindPointer, componentType, fieldName, schemaSystemName, schemaProperties, path);
    }
    return Object.values(value).flatMap((nested) =>
      checkFieldBindings(nested, componentType, fieldName, schemaSystemName, schemaProperties, path)
    );
  }
  if (Array.isArray(value)) {
    return value.flatMap((nested) => checkFieldBindings(nested, componentType, fieldName, schemaSystemName, schemaProperties, path));
  }
  return [];
}

/**
 * L3 data-contract check: every `/sphere/properties/<name>` (or well-known `/sphere/<root>`)
 * binding reachable in `components` resolves against the schema `schemaSystemName` declares, and
 * — when the catalog prop it flows into has a known type — is type-compatible with it. Never runs
 * in the write gate (Resolved Decisions: schema-service schema reads would otherwise couple
 * composition saves to schema-service); called from the composer check, publish preflight, and
 * the sweep, all of which already have a schema context to inject.
 */
export function checkDataContract(
  components: readonly A2uiComponentEntry[],
  schemaSystemName: string | undefined,
  resolveSchema: SchemaResolver,
  path = 'a2ui/components'
): ConformanceFinding[] {
  if (!schemaSystemName) return [];

  const schemaProperties = resolveSchema(schemaSystemName);

  return components.flatMap((entry) =>
    Object.entries(entry).flatMap(([fieldName, value]) => {
      if (fieldName === 'id' || fieldName === 'component') return [];
      return checkFieldBindings(value, entry.component, fieldName, schemaSystemName, schemaProperties, `${path}/${entry.id}/${fieldName}`);
    })
  );
}

export { WELL_KNOWN_SPHERE_ROOTS };
